import { useState, useEffect } from "react";

export default function SplashScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  
  useEffect(() => {
    // Phase 1: Logo appears
    setTimeout(() => setPhase(1), 100);
    // Phase 2: Logo pulses
    setTimeout(() => setPhase(2), 800);
    // Phase 3: Tagline appears
    setTimeout(() => setPhase(3), 1200);
    // Phase 4: Fade out
    setTimeout(() => setPhase(4), 2200);
    // Complete
    setTimeout(() => onComplete?.(), 2800);
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-[#0A0A0A] flex flex-col items-center justify-center transition-opacity duration-500 ${
        phase >= 4 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/3 left-1/4 w-64 h-64 bg-[#FF4500]/10 rounded-full blur-[100px] transition-all duration-1000 ${
          phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        }`} />
        <div className={`absolute bottom-1/3 right-1/4 w-48 h-48 bg-[#FF4500]/5 rounded-full blur-[80px] transition-all duration-1000 delay-300 ${
          phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        }`} />
      </div>

      {/* Logo Container */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Main Logo */}
        <div className={`relative transition-all duration-700 ease-out ${
          phase >= 1 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
        }`}>
          <h1 
            className="text-6xl sm:text-7xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            TAT
            <span className={`text-[#FF4500] inline-block transition-all duration-500 ${
              phase >= 2 ? 'animate-pulse-glow scale-110' : ''
            }`}>.</span>
          </h1>
          
          {/* Glow effect */}
          <div className={`absolute inset-0 blur-2xl bg-[#FF4500]/30 -z-10 transition-all duration-700 ${
            phase >= 2 ? 'opacity-60 scale-150' : 'opacity-0 scale-100'
          }`} />
        </div>

        {/* Atelier text */}
        <div className={`mt-2 overflow-hidden transition-all duration-500 ${
          phase >= 1 ? 'opacity-100' : 'opacity-0'
        }`}>
          <p className={`text-white/30 text-xs tracking-[0.5em] uppercase transition-all duration-700 ${
            phase >= 1 ? 'translate-y-0' : 'translate-y-full'
          }`}>
            Atelier
          </p>
        </div>

        {/* Animated line */}
        <div className={`mt-6 h-px bg-gradient-to-r from-transparent via-[#FF4500]/50 to-transparent transition-all duration-700 ${
          phase >= 2 ? 'w-24 opacity-100' : 'w-0 opacity-0'
        }`} />

        {/* Tagline */}
        <div className={`mt-4 transition-all duration-700 ${
          phase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p className="text-white/20 text-[9px] tracking-[0.3em] uppercase">
            Architecture & Design
          </p>
        </div>

        {/* Loading indicator */}
        <div className={`mt-10 flex items-center gap-1.5 transition-all duration-500 ${
          phase >= 2 && phase < 4 ? 'opacity-100' : 'opacity-0'
        }`}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#FF4500]/60 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>

      {/* Bottom branding */}
      <div className={`absolute bottom-12 transition-all duration-700 ${
        phase >= 3 ? 'opacity-100' : 'opacity-0'
      }`}>
        <p className="text-white/10 text-[8px] tracking-[0.2em] uppercase">
          The Architecture Tale
        </p>
      </div>
    </div>
  );
}
